import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { apiClient } from "@/App";
import { toast } from "sonner";
import {
  MapPin,
  Plus,
  Search,
  Calendar,
  Clock,
  CheckCircle2,
  FileText,
  Eye,
  Mail,
  Bell
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

export default function SiteVisits() {
  const location = useLocation();
  const [visits, setVisits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showNewModal, setShowNewModal] = useState(location.state?.openNew || false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    project_id: "",
    scheduled_date: "",
    scheduled_time: "",
    purpose: "",
    notes: ""
  });
  
  const [reportData, setReportData] = useState({
    report: "",
    notes: "",
    visit_summary: "",
    alert_client: false
  });

  useEffect(() => {
    fetchVisits();
    fetchProjects();
  }, [statusFilter]);

  const fetchVisits = async () => {
    try {
      let url = "/site-visits";
      if (statusFilter !== "all") url += `?status=${statusFilter}`;
      const response = await apiClient.get(url);
      setVisits(response.data);
    } catch (error) {
      console.error("Failed to fetch visits:", error);
      toast.error("Failed to load site visits");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await apiClient.get("/projects?status=active");
      setProjects(response.data);
    } catch (error) {
      console.error("Failed to fetch projects:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await apiClient.post("/site-visits", formData);
      toast.success("Site visit scheduled successfully!");
      setShowNewModal(false);
      resetForm();
      fetchVisits();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to schedule visit");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteVisit = async (e) => {
    e.preventDefault();
    if (!selectedVisit) return;
    setSubmitting(true);
    
    try {
      const response = await apiClient.put(`/site-visits/${selectedVisit.visit_id}`, {
        status: "completed",
        report: reportData.report,
        notes: reportData.notes,
        visit_summary: reportData.visit_summary,
        alert_client: reportData.alert_client
      });
      
      if (response.data.alert_sent) {
        toast.success("Visit completed & report sent to client!");
      } else {
        toast.success("Visit completed successfully!");
      }
      setShowReportModal(false);
      setSelectedVisit(null);
      setReportData({ report: "", notes: "", visit_summary: "", alert_client: false });
      fetchVisits();
    } catch (error) {
      toast.error("Failed to complete visit");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelVisit = async (visitId) => {
    if (!window.confirm("Cancel this site visit?")) return;
    
    try {
      await apiClient.put(`/site-visits/${visitId}`, { status: "cancelled" });
      toast.success("Visit cancelled");
      fetchVisits();
    } catch (error) {
      toast.error("Failed to cancel visit");
    }
  };

  const resetForm = () => {
    setFormData({
      project_id: "",
      scheduled_date: "",
      scheduled_time: "",
      purpose: "",
      notes: ""
    });
  };

  const filteredVisits = visits.filter(visit => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      visit.project_name?.toLowerCase().includes(query) ||
      visit.client_name?.toLowerCase().includes(query) ||
      visit.purpose?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="page-content" data-testid="site-visits-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Site Visits</h1>
          <p className="page-subtitle">Schedule and track all your site visits</p>
        </div>
        <Button 
          onClick={() => setShowNewModal(true)}
          className="bg-[#FF4500] hover:bg-[#E65100] btn-glow"
          data-testid="schedule-visit-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Schedule Visit
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            placeholder="Search visits..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#171717] border-[#262626]"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px] bg-[#171717] border-[#262626]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-[#262626]">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Visits List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-[#1A1A1A] rounded animate-pulse"></div>
          ))}
        </div>
      ) : filteredVisits.length > 0 ? (
        <div className="space-y-4" data-testid="visits-list">
          {filteredVisits.map((visit) => (
            <div
              key={visit.visit_id}
              className="bg-[#1A1A1A] border border-[#262626] rounded p-5 card-hover"
              data-testid={`visit-${visit.visit_id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-white">{visit.project_name}</h3>
                    <span className={`badge ${
                      visit.status === 'completed' ? 'status-completed' :
                      visit.status === 'scheduled' ? 'status-pending' :
                      'bg-gray-700 text-gray-300'
                    }`}>
                      {visit.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-400 mb-3">{visit.purpose}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {visit.scheduled_date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {visit.scheduled_time}
                    </span>
                    <span className="text-gray-400">{visit.client_name}</span>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  {visit.status === 'scheduled' && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedVisit(visit);
                          setShowReportModal(true);
                        }}
                        className="bg-[#10B981] hover:bg-[#059669]"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Complete
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCancelVisit(visit.visit_id)}
                        className="border-red-900 text-red-400 hover:bg-red-900/20"
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                  {visit.status === 'completed' && visit.report && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-[#262626]"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      View Report
                    </Button>
                  )}
                </div>
              </div>
              
              {visit.notes && (
                <div className="mt-3 p-3 bg-[#171717] rounded border border-[#262626]">
                  <p className="text-xs text-gray-500 mb-1">Notes</p>
                  <p className="text-sm text-gray-300">{visit.notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-state-icon">
            <MapPin />
          </div>
          <h3 className="empty-state-title">No site visits found</h3>
          <p className="empty-state-description">
            {searchQuery || statusFilter !== "all"
              ? "Try adjusting your filters"
              : "Schedule your first site visit to get started"}
          </p>
          <Button onClick={() => setShowNewModal(true)} className="bg-[#FF4500] hover:bg-[#E65100]">
            <Plus className="w-4 h-4 mr-2" />
            Schedule Visit
          </Button>
        </div>
      )}

      {/* New Visit Modal */}
      <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
        <DialogContent className="bg-[#1A1A1A] border-[#262626]" aria-describedby="new-visit-description">
          <DialogHeader>
            <DialogTitle className="text-white">Schedule Site Visit</DialogTitle>
            <p id="new-visit-description" className="text-sm text-gray-500">Schedule a new site visit for a project</p>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-gray-400">Project *</Label>
              <Select 
                value={formData.project_id} 
                onValueChange={(value) => setFormData({...formData, project_id: value})}
              >
                <SelectTrigger className="bg-[#171717] border-[#262626]" data-testid="project-select">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                  {projects.map((project) => (
                    <SelectItem key={project.project_id} value={project.project_id}>
                      {project.name} - {project.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400">Date *</Label>
                <Input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                  className="bg-[#171717] border-[#262626]"
                  required
                />
              </div>
              <div>
                <Label className="text-gray-400">Time *</Label>
                <Input
                  type="time"
                  value={formData.scheduled_time}
                  onChange={(e) => setFormData({...formData, scheduled_time: e.target.value})}
                  className="bg-[#171717] border-[#262626]"
                  required
                />
              </div>
            </div>
            
            <div>
              <Label className="text-gray-400">Purpose *</Label>
              <Input
                value={formData.purpose}
                onChange={(e) => setFormData({...formData, purpose: e.target.value})}
                placeholder="e.g., Site inspection, Progress review"
                className="bg-[#171717] border-[#262626]"
                required
              />
            </div>
            
            <div>
              <Label className="text-gray-400">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="bg-[#171717] border-[#262626]"
                placeholder="Additional notes..."
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNewModal(false)} className="border-[#262626]">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || !formData.project_id} className="bg-[#FF4500] hover:bg-[#E65100]">
                {submitting ? "Scheduling..." : "Schedule Visit"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Complete Visit Modal */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent className="bg-[#1A1A1A] border-[#262626] max-w-lg" aria-describedby="complete-visit-description">
          <DialogHeader>
            <DialogTitle className="text-white">Complete Site Visit</DialogTitle>
            <p id="complete-visit-description" className="text-sm text-gray-500">Add your visit report and mark as completed</p>
          </DialogHeader>
          
          <form onSubmit={handleCompleteVisit} className="space-y-4">
            <div>
              <Label className="text-gray-400">Visit Report *</Label>
              <Textarea
                value={reportData.report}
                onChange={(e) => setReportData({...reportData, report: e.target.value})}
                className="bg-[#171717] border-[#262626] min-h-[120px]"
                placeholder="Write your site visit report..."
                required
              />
            </div>
            
            <div>
              <Label className="text-gray-400">Additional Notes</Label>
              <Textarea
                value={reportData.notes}
                onChange={(e) => setReportData({...reportData, notes: e.target.value})}
                className="bg-[#171717] border-[#262626]"
                placeholder="Any additional observations..."
              />
            </div>
            
            {/* Alert Client Section */}
            <div className="space-y-3 p-4 bg-[#171717] rounded border border-[#262626]">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4 text-[#10B981]" />
                <p className="text-sm font-medium text-white">Email Report to Client</p>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white">Send Visit Report</p>
                  <p className="text-xs text-gray-500">Client will receive the site visit summary</p>
                </div>
                <Switch 
                  checked={reportData.alert_client} 
                  onCheckedChange={(checked) => setReportData({...reportData, alert_client: checked})}
                />
              </div>
              
              {reportData.alert_client && (
                <div className="mt-3">
                  <Label className="text-gray-400 text-xs">Summary for Client</Label>
                  <Textarea
                    value={reportData.visit_summary}
                    onChange={(e) => setReportData({...reportData, visit_summary: e.target.value})}
                    className="bg-[#0D0D0D] border-[#262626] mt-1"
                    placeholder="Write a summary message for the client..."
                    rows={3}
                  />
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowReportModal(false)} className="border-[#262626]">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-[#10B981] hover:bg-[#059669]">
                {submitting ? "Completing..." : reportData.alert_client ? "Complete & Send Report" : "Complete Visit"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
